import { Divider, Switch } from '@mui/material';
import { isEmptyString, isNotNullOrUndefined } from '@weft/utils';
import { get } from 'lodash';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { VayuTextField } from '../../../../../../../components/design/vayu-theme-components/VayuTextField';
import { useFormErrorNotificationContext } from '../../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { useChargeContext } from '../../../charge-context';
import type { PlanInputForm } from '../../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from '../../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { ChargeEditDrawerContentItemOptional } from '../ChargeEditDrawerContentItemOptional';
import { ProductErpIdSelector } from '../components/ProductErpIdSelector';

const YEARLY_UPFRONT_DESCRIPTION_SUFFIX = ', Yearly upfront';

export const FixedPerUnitChargeEditDrawerContent: React.FC = () => {
  const { itemIndex: productIndex } = useChargeContext();
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const { register, formState: { errors }, control, setValue, watch } = useFormContext<PlanInputForm>();

  const registerDescription = register(`fixedProducts.${productIndex}.variants.0.description`);
  registerErrorField(registerDescription.name, CREATE_PLAN_TABS.serviceFees);

  const onUpfrontPayChange = (upfrontPay: boolean) => {
    const existingDescription = watch(`fixedProducts.${productIndex}.variants.0.description`) ?? '';

    if ((isEmptyString(existingDescription) && !upfrontPay) || existingDescription.endsWith(YEARLY_UPFRONT_DESCRIPTION_SUFFIX) && upfrontPay) {
      return;
    }

    const existingDescriptionWithoutSuffix = existingDescription.replace(YEARLY_UPFRONT_DESCRIPTION_SUFFIX, '');
    const existingDescriptionWithSuffix = `${existingDescriptionWithoutSuffix}${YEARLY_UPFRONT_DESCRIPTION_SUFFIX}`;
    const newDescription = isEmptyString(existingDescription) ? 'Yearly upfront' : existingDescriptionWithSuffix;

    setValue(`fixedProducts.${productIndex}.variants.0.description`, `${upfrontPay ? newDescription : existingDescriptionWithoutSuffix}`);
  };

  return (
    <div>
      <div className="flex flex-col gap-y-6">
        <ChargeEditDrawerContentItemOptional
          fieldPath={registerDescription.name} 
          label="Description"
        >
          <VayuTextField
            {...registerDescription}
            fullWidth
            focused
            placeholder="Add a description"
            multiline
            error={isNotNullOrUndefined(get(errors, registerDescription.name))}
          />
        </ChargeEditDrawerContentItemOptional>

        <Divider/>

        <div className="flex items-center gap-x-2">
          <Controller
            control={control}
            name={`fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.upfrontPay`}
            render={({ field: { onChange, ...restOfField } }) => (
              <Switch
                checked={restOfField.value}
                onChange={(changeEvent) => {
                  onChange(changeEvent);
                  onUpfrontPayChange(changeEvent.target.checked);
                }}
                {...restOfField}
              />
            )}
          />
          <label>Yearly upfront</label>
        </div>

        <ProductErpIdSelector />
      </div>
    </div>
  );
};